import amplitudeJS from 'amplitude-js'
import createGuest from 'cross-domain-storage/guest'
import { logError } from '../core/logger'
import { generateGUID } from '~/core/utils'
import { getIsElectron } from '~/selectors/app'
import store from '~/core/store'
import { DID_STORAGE_NAME } from '~/core/constants'
import envData from '~/core/env'
import { getPrimaryProvider } from '~/selectors/calendar'

const amplitude = amplitudeJS.getInstance()

const JOIN_MEETING_ORIGINS = {
  JOIN_BY_ID: 'joinById',
  CALENDAR: 'calendar',
  RECENTS: 'recents',
  START: 'start',
  DEEPLINK: 'deeplink',
  GUEST_SCREEN: 'guestScreen',
  POPUP: 'popup'
}

const EVENT_NAMES = {
  ADD_MEETING_TO_CALENDAR: 'Add meeting to a calendar event',
  CALENDAR_SYNC: 'Sync calendar',
  CALENDAR_UNSYNC: 'Unsync calendar',
  COPY_RECORDING_TO_CLIPBOARD: 'Copy recording to clipboard',
  COPY_TO_CLIPBOARD: 'Copy personal meeting info',
  DISABLE_MIC: 'Disable mic from preview screen',
  EDIT_PERSONAL_MEETING_NAME: 'Edit personal meeting space',
  EDIT_PERSONAL_MEETING_PASSWORD: 'Edit personal meeting space password',
  ENABLE_MIC: 'Enable mic from preview screen',
  GET_RECORDING_URL: 'Retrieving presigned recording url',
  INIT: 'Init 8x8 Meet bundle',
  JOIN_EXISTING_TENANT: 'Join existing tenant',
  JOIN_MEETING: 'Join Meeting',
  LEAVE_MEETING: 'Leave meeting',
  SALESFORCE_LINK: 'Salesforce linked meeting',
  SALESFORCE_LOAD: 'Summary loaded in salesforce',
  SALESFORCE_LOGIN: 'Salesforce logged in',
  SALESFORCE_LOGOUT: 'Salesforce logged out',
  SALESFORCE_UNLINK: 'Salesforce unlinked meeting',
  SALESFORCE_VIEW: 'Salesforce view link',
  SUMMARY_FEEDBACK: 'Summary feedback submitted',
  TOGGLE_NEW_MEETINGS_EXPERIENCE: 'New meetings experience toggled',
  TOGGLE_UNDOCKED: 'Undocked mode toggled',
  UPDATE_USER_SETTING: 'Update user setting',
  UNDOCKED_REVERTED: 'Undocked mode reverted'
}

const EVENT_STATES = {
  INIT: '[INIT]',
  FAIL: '[FAIL]'
}

const PAGE_NAMES = {
  CALENDAR_FOR_MEETINGS: 'View Calendar for Meetings',
  FEEDBACK: 'View Feedback',
  POST_MEETING: 'View Post Meeting page',
  TENANT_PAGE: 'View Tenant page',
  GUEST_PLANS: 'View guest Plan selection page'
}

const SOURCE_NAMES = {
  desktop: 'VirtualOffice',
  web: 'VirtualOfficeWeb'
}

let sharedStorage

let initInProgress

const trackBatch = []

const getSharedStorage = async () => {
  if (sharedStorage) {
    return sharedStorage
  }

  const url = `${envData.webUrl}/static/accessStorage.html`

  try {
    const isStorageAvailable = await fetch(url, {
      method: 'HEAD'
    })

    if (isStorageAvailable.ok) {
      sharedStorage = createGuest(url)
      return sharedStorage
    }
  } catch (err) {
    throw new Error(err)
  }

  throw new Error('Failed to access shared storage')
}

const getDeviceID = async () => {
  let id
  try {
    const storage = await getSharedStorage()
    id = await new Promise(resolve => {
      storage.get(DID_STORAGE_NAME, (error, data) => {
        if (data) {
          resolve(data)
          return
        }
        if (error) {
          logError({ __filename, error }, 'Failed getting deviceID from shared storage.')
        }
        resolve(null)
      })
    })

    storage.close()
  } catch (e) {
    logError({ __filename, e }, 'Failed getting deviceID; shared storage unavailable')
  }
  return id
}

const setDeviceID = async id => {
  try {
    const storage = await getSharedStorage()
    storage.set(DID_STORAGE_NAME, id, error => {
      if (error) {
        logError({ __filename, error }, 'Failed setting deviceID to shared storage.')
      }

      storage.close()
    })
  } catch (e) {
    logError({ __filename, e }, 'Failed setting deviceID; shared storage unavailable')
  }
}

const updateUserIdentity = userIdentity => {
  if (initInProgress) {
    trackBatch.unshift(() => updateUserIdentity(userIdentity))
    return
  }

  const state = store.getState()
  const source = SOURCE_NAMES[getIsElectron(state) ? 'desktop' : 'web']
  let traits

  if (userIdentity) {
    const { customerId, ext, email, hasSubscription, userType } = userIdentity
    traits = {
      authenticated: true,
      googleAnalyticsId: window.googleAnalyticsId,
      source,
      customerId,
      ext,
      email,
      pbx: userIdentity.pbx || userIdentity.tenant,
      env: envData.env
    }

    if (hasSubscription !== undefined) {
      traits.hasSubscription = hasSubscription
    }

    if (userType !== undefined) {
      traits.userType = userType
    }
  } else {
    traits = {
      hasSubscription: false,
      authenticated: false,
      googleAnalyticsId: window.googleAnalyticsId,
      env: envData.env,
      source
    }
  }

  const identity = new amplitude.Identify()
  Object.keys(traits).forEach(key => identity.set(key, traits[key]))

  amplitude.setUserId(userIdentity ? userIdentity.userId : null)
  amplitude.identify(identity)
}

export const updateDeviceID = async id => {
  await setDeviceID(id)
  amplitude.setDeviceId(id)
}

export const assignIDs = async () => {
  let id = await getDeviceID()
  if (!id) {
    id = generateGUID()
    await setDeviceID(id)
  }

  amplitude.setDeviceId(id)

  return id
}

export const initAnalytics = async userIdentity => {
  initInProgress = true
  const userId = userIdentity ? userIdentity.userId : null

  amplitude.init(envData.analyticsKey, userId, {
    includeReferrer: true,
    saveParamsReferrerOncePerSession: false
  }, async () => {
    await assignIDs()
    initInProgress = false

    updateUserIdentity(userIdentity)
    while (trackBatch.length) {
      trackBatch.pop()()
    }
  })
}

const trackJoinMeeting = (state, properties) => {
  const calendarName = getPrimaryProvider(store.getState())

  trackEvent(EVENT_NAMES.JOIN_MEETING, state, { ...properties, calendarName })
}

const trackEvent = (name, state, properties = {}) => {
  if (initInProgress) {
    trackBatch.unshift(() => trackEvent(name, state, properties))
    return
  }

  const eventName = state ? `${state} ${name}` : name
  amplitude.logEvent(eventName, typeof properties === 'object' ? properties : { payload: properties })
}

const trackInitEvent = (name, properties) => {
  trackEvent(name, EVENT_STATES.INIT, properties)
}

const trackFailedEvent = (name, properties) => {
  trackEvent(name, EVENT_STATES.FAIL, properties)
}

const trackPageView = (pageName, props = {}) => {
  if (initInProgress) {
    trackBatch.unshift(() => trackPageView(pageName, props))
    return
  }
  const { referrer } = document
  const storedReferrer = localStorage?.getItem('standaloneReferrer')
  const referrerToUse = storedReferrer || referrer || null
  const mergedProps = {
    ...props,
    referrer: referrerToUse
  }

  if (storedReferrer) {
    localStorage.removeItem('standaloneReferrer')
  }

  trackEvent(pageName, false, mergedProps)
}

export default {
  EVENT_NAMES,
  PAGE_NAMES,
  EVENT_STATES,
  JOIN_MEETING_ORIGINS,
  trackEvent,
  trackInitEvent,
  trackFailedEvent,
  trackPageView,
  trackJoinMeeting,
  updateUserIdentity
}
